'use client';

import { MdFeaturedPlayList } from 'react-icons/md';
import ToggleSwitch from './backend/ToggleSwitch';

const FeatureAccessPanel = ({ clientSettings, setClientSettings }) => {
  const handleFeatureToggle = (feature, value) => {
    setClientSettings(prev => ({
      ...prev,
      featureAccess: {
        ...prev.featureAccess,
        [feature]: value,
      },
    }));
  };

  const getFeatureStatus = () => {
    const { aihEnabled, menuQuizEnabled, wayfinderEnabled } = clientSettings.featureAccess || {};

    const enabledCount = [aihEnabled, menuQuizEnabled, wayfinderEnabled].filter(Boolean).length;

    if (enabledCount === 3)
      return { text: 'All Systems', color: 'success', icon: 'fa-check-double' };
    if (enabledCount === 0) return { text: 'No Systems', color: 'danger', icon: 'fa-times' };

    // Two systems enabled
    if (enabledCount === 2) {
      if (aihEnabled && menuQuizEnabled)
        return { text: 'AiH + Menu', color: 'success', icon: 'fa-check-double' };
      if (aihEnabled && wayfinderEnabled)
        return { text: 'AiH + Wayfinder', color: 'success', icon: 'fa-check-double' };
      if (menuQuizEnabled && wayfinderEnabled)
        return { text: 'Menu + Wayfinder', color: 'success', icon: 'fa-check-double' };
    }

    // Single system enabled
    if (aihEnabled) return { text: 'AiH Only', color: 'info', icon: 'fa-robot' };
    if (menuQuizEnabled) return { text: 'Menu/Quiz Only', color: 'warning', icon: 'fa-list' };
    if (wayfinderEnabled)
      return { text: 'Wayfinder Only', color: 'primary', icon: 'fa-map-marked-alt' };

    return { text: 'No Systems', color: 'danger', icon: 'fa-times' };
  };

  const status = getFeatureStatus();

  return (
    <div className="settings-section card fade-in" style={{ marginTop: '3rem' }}>
      <div className="card-header d-flex align-items-center justify-content-between mb-4">
        <h5 className="card-title">
          <span style={{ marginRight: '10px', fontSize: '23px' }}>
            <MdFeaturedPlayList />
          </span>
          <span style={{ marginTop: '5px' }}>Feature Access Control</span>
        </h5>
        <div className={`badge bg-${status.color} fs-6`}>
          <i className={`fas ${status.icon} me-2`}></i>
          {status.text}
        </div>
      </div>

      {/* Main Feature Toggles */}
      <div className="card-body row mb-4">
        <div className="col-md-4">
          <div className="card border-info h-100" style={{ borderColor: '#e3f2fd !important' }}>
            <div
              className="card-header text-dark"
              style={{ backgroundColor: '#e3f2fd', borderColor: '#e3f2fd' }}
            >
              <h6 className="mb-0" style={{ color: '#1976d2' }}>
                <i className="fas fa-robot me-2"></i>
                AI Helper (AiH) System
              </h6>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <label className="form-label fw-bold mb-0" htmlFor="aih-enabled">
                  Enable AiH System
                </label>
                <div className="form-check form-switch">
                  <ToggleSwitch
                    label="Enable"
                    isOn={clientSettings.featureAccess?.aihEnabled || false}
                    id="aih-enabled"
                    onChange={value => handleFeatureToggle('aihEnabled', value)}
                  />
                </div>
              </div>

              <div
                className="alert"
                style={{ backgroundColor: '#e3f2fd', borderColor: '#bbdefb', color: '#1565c0' }}
              >
                <small>
                  <i className="fas fa-info-circle me-2"></i>
                  AiH provides AI-powered assistance and conversation capabilities
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card border-light h-100" style={{ borderColor: '#fff3e0 !important' }}>
            <div
              className="card-header text-dark"
              style={{ backgroundColor: '#fff3e0', borderColor: '#fff3e0' }}
            >
              <h6 className="mb-0" style={{ color: '#f57c00' }}>
                <i className="fas fa-list me-2"></i>
                Menu/Quiz System
              </h6>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <label className="form-label fw-bold mb-0" htmlFor="menu-quiz-enabled">
                  Enable Menu/Quiz System
                </label>
                <div className="form-check form-switch">
                  <ToggleSwitch
                    label="Enable"
                    isOn={clientSettings.featureAccess?.menuQuizEnabled || false}
                    id="menu-quiz-enabled"
                    onChange={value => handleFeatureToggle('menuQuizEnabled', value)}
                  />
                </div>
              </div>

              <div
                className="alert"
                style={{ backgroundColor: '#fff3e0', borderColor: '#ffcc02', color: '#ef6c00' }}
              >
                <small>
                  <i className="fas fa-info-circle me-2"></i>
                  Menu/Quiz provides interactive menu navigation and quiz functionality
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card border-primary h-100" style={{ borderColor: '#e8f5e9 !important' }}>
            <div
              className="card-header text-dark"
              style={{ backgroundColor: '#e8f5e9', borderColor: '#e8f5e9' }}
            >
              <h6 className="mb-0" style={{ color: '#2e7d32' }}>
                <i className="fas fa-map-marked-alt me-2"></i>
                Wayfinder System
              </h6>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <label className="form-label fw-bold mb-0" htmlFor="wayfinder-enabled">
                  Enable Wayfinder System
                </label>
                <div className="form-check form-switch">
                  <ToggleSwitch
                    label="Enable"
                    isOn={clientSettings.featureAccess?.wayfinderEnabled || false}
                    id="wayfinder-enabled"
                    onChange={value => handleFeatureToggle('wayfinderEnabled', value)}
                  />
                </div>
              </div>

              <div
                className="alert"
                style={{ backgroundColor: '#e8f5e9', borderColor: '#c8e6c9', color: '#1b5e20' }}
              >
                <small>
                  <i className="fas fa-info-circle me-2"></i>
                  Wayfinder provides interactive location navigation and directory services
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureAccessPanel;
